import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import decode from 'jwt-decode';
import {
  Card,
  CardContent,
  TextField,
  Button,
  CircularProgress,
  Typography,
  Box
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { darken } from '@material-ui/core/styles/colorManipulator';
import clsx from 'clsx';
import Error from 'app/fuse-layouts/shared-components/Error';
import * as ActionsDialog from 'app/store/actions';
import { API_BASE_URL } from '@config';
import jwtService from 'app/auth/store/actions/jwtService';
import { setUserData } from 'app/auth/store/actions/user.actions';
import CampaignVerificationCode from './CampaignVerificationCode';

const useStyles = makeStyles((theme) => ({
  root: {
    background: `linear-gradient(to right, ${theme.palette.primary.dark} 0%, ${darken(
      theme.palette.primary.dark,
      0.5
    )} 100%)`,
    color: theme.palette.primary.contrastText
  },
  card: {
    maxWidth: 600,
    width: '100%',
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    overflow: 'visible'
  },
  cardWithIcon: {
    borderRadius: '16px',
    overflow: 'visible',
    position: 'relative'
  },
  headerWithBranding: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 32px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default
  },
  heidiLogoSmall: {
    width: 80,
    height: 'auto'
  },
  customIcon: {
    width: 140,
    height: 140,
    objectFit: 'contain',
    marginBottom: theme.spacing(3)
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    width: '100%',
    maxWidth: 450
  },
  inputField: {
    '& .MuiInputBase-input': {
      fontSize: '2rem',
      padding: '20px 18px'
    },
    '& .MuiInputLabel-outlined': {
      fontSize: '1.9rem'
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px'
    }
  },
  button: {
    marginTop: theme.spacing(1),
    padding: '18px',
    fontSize: '1.3rem',
    fontWeight: 600,
    borderRadius: '12px',
    textTransform: 'none'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(4)
  },
  campaignHeader: {
    marginBottom: theme.spacing(3),
    textAlign: 'center'
  },
  welcomeMessage: {
    fontSize: '1.8rem',
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary
  },
  campaignName: {
    fontSize: '2rem',
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main
  },
  helperText: {
    fontSize: '1.15rem',
    color: theme.palette.text.secondary,
    marginTop: '-8px',
    textAlign: 'center'
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(2),
    textAlign: 'center',
    fontSize: '0.95rem'
  }
}));

const CampaignEmailLink = () => {
  const classes = useStyles();
  const { campaignSlug } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState(null); // 'existing', 'new', 'verify', 'error'
  const [campaign, setCampaign] = useState(null);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [emailSource, setEmailSource] = useState(null); // 'url' or 'manual'

  // Extract email from URL parameter or set to enter-email mode if not provided
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailParam = params.get('email');

    if (!emailParam) {
      // No email in URL - show form to enter email
      setLoading(true);
      // Fetch campaign data using public endpoint
      fetch(`${API_BASE_URL}/api/campaigns/by-slug/${campaignSlug}`)
        .then((res) => {
          if (!res.ok) {
            throw new Error('Campaign not found or is inactive');
          }
          return res.json();
        })
        .then((data) => {
          setCampaign({
            id: data._id,
            name: data.name,
            authPageCustomization: data.authPageCustomization
          });
          setMode('enter-email');
        })
        .catch((err) => {
          console.error('Campaign fetch error:', err);
          setError(err.message || 'Failed to load campaign. Please try again.');
          setMode('error');
        })
        .finally(() => setLoading(false));
      return;
    }

    // Email provided in URL - proceed with checking
    const decodedEmail = decodeURIComponent(emailParam);
    setEmail(decodedEmail);
    setEmailSource('url');
    checkEmail(decodedEmail);
  }, [campaignSlug, location.search]);

  const checkEmail = async (emailToCheck) => {
    try {
      setChecking(true);
      const response = await fetch(`${API_BASE_URL}/api/auth/campaign/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailToCheck,
          campaignSlug: campaignSlug
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Campaign not found or is inactive');
        setMode('error');
        return;
      }

      setCampaign((prevCampaign) => ({
        ...prevCampaign,
        id: data.campaignId,
        name: data.campaignName
      }));

      if (data.exists) {
        setMode('existing');
        // Auto-send code for existing users
        sendCodeForExisting(emailToCheck);
      } else {
        setMode('new');
      }
    } catch (err) {
      console.error('Check email error:', err);
      setError('Failed to process your request. Please try again.');
      setMode('error');
    } finally {
      setChecking(false);
      setLoading(false);
    }
  };

  const sendCodeForExisting = async (emailToUse) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/campaign/resend-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailToUse,
          campaignSlug: campaignSlug
        })
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.message || 'Failed to send verification code');
      }
    } catch (err) {
      console.error('Send code error:', err);
      // Don't show error to user - code may still have been sent
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setCreatingAccount(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/campaign/create-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          fullName: fullName,
          campaignSlug: campaignSlug
        })
      });

      const createData = await response.json();

      if (!response.ok) {
        setError(createData.message || 'Failed to create account');
        setCreatingAccount(false);
        return;
      }

      // Show code verification form
      setMode('verify');
      setCreatingAccount(false);
    } catch (err) {
      console.error('Create account error:', err);
      setError('Failed to create account. Please try again.');
      setCreatingAccount(false);
    }
  };

  const handleVerifySuccess = (token) => {
    // Store token properly using jwtService (sets localStorage and axios header)
    jwtService.setSession(token);

    // Decode token to get user data
    const decoded = decode(token);
    const user = decoded.user;

    // Set user data in Redux store
    dispatch(setUserData(user));

    // Set flag to show campaign showcase dialog on dashboard
    localStorage.setItem('showCampaignShowcase', 'true');

    // Redirect to dashboard instead of landing page
    history.push('/dashboard');
  };

  if (loading) {
    return (
      <div className={clsx(classes.root, 'flex flex-col flex-auto flex-shrink-0 items-center justify-center p-32')}>
        <div className="flex flex-col items-center justify-center w-full">
          <Card className={classes.card}>
            <CardContent className={classes.loadingContainer}>
              <CircularProgress />
              <Typography>Loading campaign...</Typography>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (mode === 'error') {
    return (
      <div className={clsx(classes.root, 'flex flex-col flex-auto flex-shrink-0 items-center justify-center p-32')}>
        <div className="flex flex-col items-center justify-center w-full">
          <Card className={classes.card}>
            <CardContent className="flex flex-col items-center justify-center">
              <img className="w-128 m-32" src="assets/images/logos/heidi.svg" alt="logo" />
              <Error
                message={error}
                clearError={() => {
                  setError('');
                  setMode('enter-email');
                }}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Mode: enter email (no email provided in URL)
  if (mode === 'enter-email') {
    const handleEmailSubmit = (e) => {
      e.preventDefault();
      if (email.trim()) {
        setEmailSource('manual');
        checkEmail(email.trim());
      }
    };

    const customIcon = campaign?.authPageCustomization?.icon;
    const welcomeMsg = campaign?.authPageCustomization?.welcomeMessage;
    const helperText = campaign?.authPageCustomization?.helperText || "We'll send a verification code to this email";

    return (
      <div className={clsx(classes.root, 'flex flex-col flex-auto flex-shrink-0 items-center justify-center p-32')}>
        <div className="flex flex-col items-center justify-center w-full">
          <Card className={clsx(classes.card, customIcon && classes.cardWithIcon)}>
            {customIcon && (
              <div className={classes.headerWithBranding}>
                <img className={classes.heidiLogoSmall} src="assets/images/logos/heidi.svg" alt="Heidi AI" />
              </div>
            )}
            <CardContent className="flex flex-col items-center justify-center p-48">
              {customIcon ? (
                <img className={classes.customIcon} src={customIcon} alt={campaign?.name} />
              ) : (
                <img className="w-128 m-32" src="assets/images/logos/heidi.svg" alt="logo" />
              )}
              {campaign && (
                <div className={classes.campaignHeader}>
                  {welcomeMsg && (
                    <Typography className={classes.welcomeMessage}>
                      {welcomeMsg}
                    </Typography>
                  )}
                  <Typography className={classes.campaignName}>
                    {campaign.name}
                  </Typography>
                </div>
              )}
            <Typography variant="body1" style={{ fontSize: '1.3rem', marginBottom: '24px' }} color="textSecondary">
              Enter your email to get started
            </Typography>
            {error && (
              <Typography className={classes.errorText}>
                {error}
              </Typography>
            )}
            <form onSubmit={handleEmailSubmit} className={classes.form}>
              <TextField
                className={classes.inputField}
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
                disabled={checking}
                placeholder="you@example.com"
                variant="outlined"
                InputProps={{
                  style: { fontSize: '1.4rem !important' }
                }}
                InputLabelProps={{
                  style: { fontSize: '1.3rem !important' }
                }}
              />
              <Typography className={classes.helperText}>
                {helperText}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={checking || !email.trim()}
                className={classes.button}
                size="large"
              >
                {checking ? (
                  <>
                    <CircularProgress size={24} style={{ marginRight: 8 }} color="inherit" />
                    Checking...
                  </>
                ) : (
                  'Continue'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
        </div>
      </div>
    );
  }

  // Mode: existing account
  if (mode === 'existing') {
    return (
      <div className={clsx(classes.root, 'flex flex-col flex-auto flex-shrink-0 items-center justify-center p-32')}>
        <div className="flex flex-col items-center justify-center w-full">
          <Card className={classes.card}>
            <CardContent className="flex flex-col items-center justify-center">
              <img className="w-128 m-32" src="assets/images/logos/heidi.svg" alt="logo" />
              {campaign && (
                <div className={classes.campaignHeader}>
                  <Typography variant="h5" gutterBottom>
                    Welcome Back to {campaign.name}
                  </Typography>
                </div>
              )}
            <Typography variant="body1" paragraph>
              We found an account for <strong>{email}</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              We've sent a verification code to your email. Please enter it below:
            </Typography>
            {error && (
              <Typography className={classes.errorText} variant="body2">
                {error}
              </Typography>
            )}
            <CampaignVerificationCode
              email={email}
              campaignSlug={campaignSlug}
              onSuccess={handleVerifySuccess}
              onError={(err) => setError(err)}
            />
          </CardContent>
        </Card>
        </div>
      </div>
    );
  }

  // Mode: new account creation
  if (mode === 'new') {
    const customIcon = campaign?.authPageCustomization?.icon;
    const welcomeMsg = campaign?.authPageCustomization?.welcomeMessage;
    const helperText = campaign?.authPageCustomization?.helperText || "We'll send a verification code to this email";
    
    return (
      <div className={clsx(classes.root, 'flex flex-col flex-auto flex-shrink-0 items-center justify-center p-32')}>
        <div className="flex flex-col items-center justify-center w-full">
          <Card className={clsx(classes.card, customIcon && classes.cardWithIcon)}>
            {customIcon && (
              <div className={classes.headerWithBranding}>
                <img className={classes.heidiLogoSmall} src="assets/images/logos/heidi.svg" alt="Heidi AI" />
              </div>
            )}
            <CardContent className="flex flex-col items-center justify-center p-48">
              {customIcon ? (
                <img className={classes.customIcon} src={customIcon} alt={campaign.name} />
              ) : (
                <img className="w-128 m-32" src="assets/images/logos/heidi.svg" alt="logo" />
              )}
              {campaign && (
                <div className={classes.campaignHeader}>
                  {welcomeMsg && (
                    <Typography className={classes.welcomeMessage}>
                      {welcomeMsg}
                    </Typography>
                  )}
                  <Typography className={classes.campaignName}>
                    {campaign.name}
                  </Typography>
                </div>
              )}
            <Typography variant="body1" style={{ fontSize: '1.3rem', marginBottom: '8px' }} color="textSecondary">
              Create your account to get started
            </Typography>
            {error && (
              <Typography className={classes.errorText}>
                {error}
              </Typography>
            )}
            <form onSubmit={handleCreateAccount} className={classes.form}>
              <TextField
                className={classes.inputField}
                label="Full Name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                fullWidth
                required
                disabled={creatingAccount}
                placeholder="Enter your full name"
                variant="outlined"
                InputProps={{
                  style: { fontSize: '1.4rem !important' }
                }}
                InputLabelProps={{
                  style: { fontSize: '1.3rem !important' }
                }}
              />
              <TextField
                className={classes.inputField}
                label="Email Address"
                value={email}
                fullWidth
                disabled
                variant="outlined"
                InputProps={{
                  style: { fontSize: '1.4rem !important' }
                }}
                InputLabelProps={{
                  style: { fontSize: '1.3rem !important' }
                }}
              />
              <Typography className={classes.helperText}>
                {helperText}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={creatingAccount || !fullName.trim()}
                className={classes.button}
                size="large"
              >
                {creatingAccount ? (
                  <>
                    <CircularProgress size={24} style={{ marginRight: 8 }} color="inherit" />
                    Creating Account...
                  </>
                ) : (
                  'Continue'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
        </div>
      </div>
    );
  }

  // Mode: verify code (after new account creation or for existing user)
  if (mode === 'verify') {
    return (
      <div className={clsx(classes.root, 'flex flex-col flex-auto flex-shrink-0 items-center justify-center p-32')}>
        <div className="flex flex-col items-center justify-center w-full">
          <Card className={classes.card}>
            <CardContent className="flex flex-col items-center justify-center">
              <img className="w-128 m-32" src="assets/images/logos/heidi.svg" alt="logo" />
              {campaign && (
                <div className={classes.campaignHeader}>
                  <Typography variant="h5" gutterBottom>
                    Verify Your Email
                  </Typography>
                </div>
              )}
            <Typography variant="body1" paragraph>
              We've sent a verification code to <strong>{email}</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Enter the 6-digit code from your email:
            </Typography>
            {error && (
              <Typography className={classes.errorText} variant="body2">
                {error}
              </Typography>
            )}
            <CampaignVerificationCode
              email={email}
              campaignSlug={campaignSlug}
              onSuccess={handleVerifySuccess}
              onError={(err) => setError(err)}
            />
          </CardContent>
        </Card>
        </div>
      </div>
    );
  }

  return null;
};

export default CampaignEmailLink;
